import React, { useContext, useEffect, useState } from 'react';

import { resetPassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Betterflies5 } from '../../../../../assets/icons/v3/betterflies-5.svg';
import { ReactComponent as PasswordHide } from '../../../../../assets/icons/v3/password-hide.svg';
import { ReactComponent as PasswordShow } from '../../../../../assets/icons/v3/password-show.svg';
import BdsInput from '../../../../../shared/components/v3/BdsInput/BdsInput';
import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsTextButton from '../../../../../shared/components/v3/Buttons/BdsTextButton/BdsTextButton';
import { RESET_PASSWORD_ROUTE } from '../../../../../shared/constants/auth';
import { RECAPTCHA_SITE_KEY } from '../../../../../shared/constants/constants';
import { ARROW_LEFT_ICON } from '../../../../../shared/constants/images';
import { CLICK_ACTION, FORGOT_PASSWORD_PAGE, PASSWORD_CONTINUE } from '../../../../../shared/constants/tagManager';
import { SessionContext } from '../../../../../shared/context/auth/authContext';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_AUTH } from '../../../../../shared/translations/auth';
import { GenericVoidFunction } from '../../../../../shared/types/genericTypes';
import './EnterPassword.scss';

interface EnterPasswordProps {
  email: string;
  changeStep: GenericVoidFunction;
}

const EnterPassword = ({ email, changeStep }: EnterPasswordProps) => {
  const [userPassword, setUserPassword] = useState('');
  const [error, setError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingForgot, setLoadingForgot] = useState(false);
  const [loading, setLoading] = useState(false);

  const { sessionLogin, errorCode, resetError } = useContext(SessionContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleChangePassword = (password: string) => {
    setUserPassword(password);
    resetError('');
  };

  const handleSubmitSignIn = async () => {
    TagManagerService.pushEvent(CLICK_ACTION, PASSWORD_CONTINUE);
    window.grecaptcha.ready(async () => {
      setLoading(true);
      const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
      await sessionLogin(email, userPassword, recaptchaToken);
      setLoading(false);
    });
  };

  const handleValidation = (value: string | null) => {
    if (!value) {
      return null;
    }
    return error ? t(TRANSLATIONS_AUTH.enterPassword.invalidCredentials) : null;
  };

  const handleKeyPress = async () => {
    handleSubmitSignIn();
  };

  const handleEyeClick = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = async () => {
    resetError('');
    setLoadingForgot(true);
    TagManagerService.pushEvent(CLICK_ACTION, FORGOT_PASSWORD_PAGE);
    await resetPassword({ username: email });
    navigate(RESET_PASSWORD_ROUTE, { state: { email } });
    setLoadingForgot(false);
  };

  const handleGoBack = () => {
    resetError('');
    changeStep(0);
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      if (errorCode.toString().includes('NotAuthorizedException')) {
        setError(true);
      } else if (errorCode.toString() !== '') {
        setError(true);
      } else {
        setError(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [errorCode]);

  return (
    <div className="enter-password-container">
      <div className="auth-card-container enter-password-card">
        <div className="auth-card-go-back">
          <BdsTextButton action={handleGoBack} iconLeft={ARROW_LEFT_ICON}>
            {t(TRANSLATIONS_AUTH.goBack)}
          </BdsTextButton>
        </div>
        <div className="top-right-images">
          <Betterflies5 className="image" />
        </div>

        <div className="enter-password-text-container">
          <div className="enter-password-title">{t(TRANSLATIONS_AUTH.enterPassword.title)}</div>
          <div className="enter-password-description">{t(TRANSLATIONS_AUTH.enterPassword.description)}</div>
        </div>
        <BdsInput
          hintText={t(TRANSLATIONS_AUTH.enterPassword.placeholder)}
          onChanged={handleChangePassword}
          validator={handleValidation}
          onSubmitPressed={handleKeyPress}
          enabled={true}
          type={showPassword ? 'text' : 'password'}
          onSuffixIconClick={handleEyeClick}
          suffixIcon={showPassword ? <PasswordHide /> : <PasswordShow />}
        />

        <div className="enter-password-text-button">
          <BdsTextButton action={handleForgotPassword} loading={loadingForgot}>
            {t(TRANSLATIONS_AUTH.enterPassword.forgotPassword)}
          </BdsTextButton>
        </div>
        <BdsButton size="l" variant="primary" action={handleSubmitSignIn} disabled={!userPassword} loading={loading}>
          {t(TRANSLATIONS_AUTH.enterPassword.button)}
        </BdsButton>
      </div>
    </div>
  );
};

export default EnterPassword;
